/**
 * 플레이리스트 요청 URL 을 만든다.
 *
 * `URL` 을 쓰지 않는다 — 코어는 `lib: ES2022` 로 컴파일되므로 `URL` 이 없다.
 * `packages/contracts` 의 `buildTrackingUrl` 도 같은 이유로 손으로 조립한다.
 *
 * **`deviceId` 를 빼지 않는다.** 현재 서버는 로깅만 하지만 개인화 도입 시 클라이언트를
 * 고치지 않기 위해 계약에 남아 있다 (CLAUDE.md 절대 규칙 2).
 */
export function buildPlaylistUrl(serverUrl, channelId, deviceId, startFromProductId) {
    const base = serverUrl.replace(/\/+$/, '');
    const query = [`deviceId=${encodeURIComponent(deviceId)}`];
    if (startFromProductId) {
        query.push(`startFromProductId=${encodeURIComponent(startFromProductId)}`);
    }
    return `${base}/v1/channels/${encodeURIComponent(channelId)}/playlist?${query.join('&')}`;
}
/**
 * v34. 창 목록 요청 URL (`GET /v1/channels/{channelId}/live-events` · 절대 규칙 87).
 * 계정의 것이 없어 `deviceId` 를 싣지 않는다 — 폴링이라 상관관계 키도 필요 없다.
 */
export function buildLiveEventsUrl(serverUrl, channelId) {
    const base = serverUrl.replace(/\/+$/, '');
    return `${base}/v1/channels/${encodeURIComponent(channelId)}/live-events`;
}
/**
 * 플레이리스트 요청과 마지막 응답 보관.
 *
 * 캐싱의 목적은 요청을 줄이는 것이 아니라 **`playlistId` 를 붙들고 있는 것**이다.
 * 모든 이벤트가 그 값으로 상관관계를 맺으므로, 목록과 `playlistId` 가 한 몸으로
 * 갱신되지 않으면 이벤트가 엉뚱한 플레이리스트에 귀속된다.
 */
export class PlaylistClient {
    network;
    serverUrl;
    channelId;
    deviceId;
    last = null;
    /** v33. 마지막 응답을 **받은 순간의 기기 시계**. `serverSkewMs` 의 반쪽이다 */
    receivedAtMs = 0;
    /**
     * v34. 마지막 **창 목록** 응답(`fetchLive`)과 받은 순간. 플레이리스트 응답보다 새것이면
     * `live`·`serverSkewMs` 가 이쪽을 쓴다 — 두 응답이 같은 창을 같은 함수(`liveWindowsOf`)로
     * 만들므로, 어느 쪽이든 **더 최근 것**이 참이다.
     */
    lastLive = null;
    constructor(network, serverUrl, channelId, deviceId) {
        this.network = network;
        this.serverUrl = serverUrl;
        this.channelId = channelId;
        this.deviceId = deviceId;
    }
    /**
     * 목록을 새로 받아 보관한다. `startFromProductId` 가 주어지면 서버가 회전시킨 목록을
     * 돌려준다 (특허 ④ 제2 플레이리스트). **클라이언트에서 인덱스만 옮기지 않는다.**
     */
    async fetch(startFromProductId) {
        const url = buildPlaylistUrl(this.serverUrl, this.channelId, this.deviceId, startFromProductId);
        const res = await this.network.get(url);
        this.last = res;
        this.receivedAtMs = Date.now();
        return res;
    }
    /**
     * ★ v34. **창 목록만 새로 받는다** (절대 규칙 87). 앱의 시계가 1초마다 부른다
     * (`receiver.refreshLive()`). 목록·`playlistId` 는 건드리지 않는다 — 창은 채널의 것이고
     * 플레이리스트는 재생 순서의 것이다.
     */
    async fetchLive() {
        const res = await this.network.get(buildLiveEventsUrl(this.serverUrl, this.channelId));
        this.lastLive = { res, receivedAtMs: Date.now() };
        return res.live;
    }
    /** 두 응답 중 더 최근 것 — `live` 와 `serverSkewMs` 가 같은 쪽을 본다 */
    get liveIsNewer() {
        return this.lastLive !== null && this.lastLive.receivedAtMs >= this.receivedAtMs;
    }
    get playlistId() {
        return this.last?.playlistId ?? null;
    }
    /**
     * 이어볼 지점 (v16, 특허 청구항 1 (b)). **이력이 없거나 홈쇼핑 채널이면 `null`** 이다.
     *
     * 별도 조회를 만들지 않은 것이 D3 이다 — 채널을 켤 때마다 왕복이 둘이 되고, **둘 사이에
     * 목록이 바뀌면** resume 이 목록에 없는 항목을 가리킨다.
     */
    get resume() {
        return this.last?.resume ?? null;
    }
    /** 이 목록의 채널 종류 (v16). 앱이 버튼 구성을 이것으로 가른다 */
    get channelKind() {
        return this.last?.channelKind ?? null;
    }
    /**
     * 이 채널의 광고 편성 정책 (v21). **`null` 이면 광고가 없다** — 홈쇼핑이거나, 광고
     * 서버가 없거나, 정책이 전부 0 이다 (서버가 한 함수로 정한다).
     *
     * `resume`·`channelKind` 와 **같은 자리로 온다** — 코어는 `PlaylistClient` 를 통해서만
     * 응답을 보므로 게터가 하나 는다. 별도 조회를 만들지 않는 것이 D3 의 방침이다.
     */
    get adPolicy() {
        return this.last?.adPolicy ?? null;
    }
    /**
     * 이 채널의 상품 오버레이 노출 정책 (v23). **`null` 이면 상품 큐가 없다** — 홈쇼핑이거나,
     * 브로커 주소가 비었거나, 노출 시간이 0 이다 (서버가 한 함수로 정한다).
     *
     * **`items[].cues` 와 짝이다** — `null` 이면 큐도 전부 빈 배열이라, 한쪽만 있는 상태가
     * 원리적으로 없다 (`docs/47` §5.2).
     */
    get offerPolicy() {
        return this.last?.offerPolicy ?? null;
    }
    /**
     * 이 목록을 실제로 내준 채널 (v9). **요청에 실은 값이 아니라 응답의 값이다** —
     * 둘은 같아야 하지만, 이벤트에 남길 것은 서버가 무엇을 줬는가다.
     */
    get servedChannelId() {
        return this.last?.channelId ?? null;
    }
    get items() {
        return this.last?.items ?? [];
    }
    /**
     * v33. 앞으로 남은 **라이브 이벤트 창들**. 없으면 빈 배열이다 (절대 규칙 83).
     *
     * `adPolicy`·`offerPolicy` 와 **같은 자리로 온다** — 코어는 이 클래스를 통해서만 응답을
     * 보므로 게터가 하나 는다. 별도 조회를 만들지 않는 것이 D3 의 방침이다.
     */
    get live() {
        // v34 — 1초 조회가 더 새것이면 그것이다.
        if (this.liveIsNewer && this.lastLive !== null)
            return this.lastLive.res.live;
        return this.last?.live ?? [];
    }
    /**
     * v33. **서버 시계와의 차이(ms)** — `generatedAt − 받은 순간의 기기 시계`.
     *
     * ── ★ 왜 이 값이 필요한가 ───────────────────────────────────
     *
     * 바이패스의 진입·복귀는 **기기 시계**로 판정한다(편성표의 눈금이 그렇게 정의돼 있고 ·
     * 절대 규칙 73 ①). 그런데 데모 노트북의 시계가 3분 틀어져 있으면 **라이브가 3분 늦게
     * 시작하고 증상이 조용하다.** 수 분의 오차만 잡으면 되므로 왕복 지연은 보정하지 않는다
     * (수백 ms 이고 세그먼트가 2초다).
     *
     * ── ★★ `Date` 헤더가 아니라 `generatedAt` 이다 ──────────────
     *
     * 설계(`docs/67` §7.4)는 응답 헤더를 적었는데 **코어는 헤더를 볼 수 없다** —
     * `NetworkAdapter.get<T>` 가 파싱된 본문만 준다. 그것을 넓히면 iOS/Android 이식의 근거가
     * 약해진다(절대 규칙 6·36 · 「`NetworkAdapter` 를 넓히지 않는다」). `generatedAt` 은
     * **이미 계약에 있는 서버의 시각**이고 같은 값을 말한다.
     *
     * @returns 응답이 없거나 시각을 못 읽으면 0 — **보정하지 않는 것이 안전한 쪽**이다.
     */
    get serverSkewMs() {
        // v34 — 창 목록 응답이 더 새것이면 그 시각으로 보정한다(1초마다 새로 맞춰진다).
        const fresh = this.liveIsNewer && this.lastLive !== null ? this.lastLive : null;
        const at = fresh?.res.generatedAt ?? this.last?.generatedAt;
        if (at === undefined)
            return 0;
        const parsed = Date.parse(at);
        const receivedAt = fresh?.receivedAtMs ?? this.receivedAtMs;
        return Number.isFinite(parsed) ? parsed - receivedAt : 0;
    }
}
//# sourceMappingURL=playlist.js.map